/**
 * Automatismes (`/student/automatismes`, migration 0323) : intro → 10
 * questions flash (popup XP, chrono, « sans calculatrice ») → résultat
 * (score, XP, HORA par SKILLCARD). Pas de rapport. Une série en cours se
 * reprend ; « Arrêter » ramène au tableau de bord.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Calculator, Lock, PauseCircle, RotateCw, Timer, Zap } from 'lucide-react';
import {
  Button,
  FeedbackPopup,
  MarkdownMath,
  MathText,
  computeXpReward,
  type XpReward,
} from '@jeraisonne/ui';
import { formatHoraGain } from '@jeraisonne/db/cards';
import { ChoiceButton } from '@/components/exercise/choice-button';
import { PaperChoices, PaperColumn, PaperPage, usePaperPlayerForMe } from '@/features/paper-player';
import { freeSheetKey } from '@/features/scratch-sheet/use-free-sheet';
import { QuestionVisuals } from '@/components/exercise/question-visuals';
import { Spinner } from '@/components/spinner';
import { useAuth } from '@/lib/auth';
import { isNativeApp } from '@/lib/platform';
import { HelpTip } from '@/components/help-tip';
import { ProgrammeOffersSection } from '@/features/skillcards/programme-offers-section';
import { SubscriptionAlternative } from '@/features/skillcards/skillcard-offers-panel';
import { resumeIndex } from '@/lib/skill-diagnostic';
import {
  AutomatismesError,
  automatismesCta,
  automatismesErrorMessage,
  automatismesExamHint,
  automatismesLevelsLabel,
  automatismesLocked,
  automatismesStateKey,
  nextFreeLabel,
  finishAutomatismes,
  rewardsLeftToday,
  startAutomatismes,
  submitAutomatismeAnswer,
  useAutomatismesState,
  type AutomatismesPlay,
  type AutomatismesResult,
} from '@/lib/automatismes';

type Phase = 'intro' | 'loading' | 'playing' | 'finishing' | 'result' | 'error';

/** « 3:07 » */
export function formatChrono(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export function AutomatismesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const state = useAutomatismesState(user?.id);
  const [phase, setPhase] = useState<Phase>('intro');
  const [play, setPlay] = useState<AutomatismesPlay | null>(null);
  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  // Exercice « sur la feuille de brouillon » (drapeau `paper_player`).
  const paper = usePaperPlayerForMe();
  const [revealed, setRevealed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AutomatismesResult | null>(null);
  const [streak, setStreak] = useState(0);
  const [sessionXp, setSessionXp] = useState(0);
  const [startedAt, setStartedAt] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [reward, setReward] = useState<{
    value: XpReward;
    wasCorrect: boolean;
    explanationMd?: string;
    errorFeedbackMd?: string;
  } | null>(null);
  const shownAt = useRef(Date.now());

  const refresh = useCallback(() => {
    void qc.invalidateQueries({ queryKey: automatismesStateKey(user?.id ?? '') });
  }, [qc, user?.id]);

  // Chrono de la série (indicatif : au brevet la partie dure 20 minutes).
  useEffect(() => {
    if (phase !== 'playing') return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [phase]);

  function fail(e: unknown) {
    // Plus de série gratuite ce mois-ci : retour à l'accueil, qui propose de débloquer.
    if (e instanceof AutomatismesError && e.code === 'LIMIT_REACHED') {
      refresh();
      setPhase('intro');
      return;
    }
    setError(automatismesErrorMessage(e instanceof AutomatismesError ? e.code : ''));
    setPhase('error');
  }

  async function begin(restart: boolean) {
    setPhase('loading');
    setError(null);
    try {
      const p = await startAutomatismes(restart);
      setPlay(p);
      setStreak(0);
      setSessionXp(0);
      const i = resumeIndex(p);
      if (i >= p.questions.length) {
        await finish(p.session.id);
        return;
      }
      setIndex(i);
      setSelected(null);
      setRevealed(false);
      shownAt.current = Date.now();
      setStartedAt(Date.now());
      setNow(Date.now());
      setPhase('playing');
      refresh();
    } catch (e) {
      fail(e);
    }
  }

  async function finish(sessionId: string) {
    setPhase('finishing');
    try {
      const r = await finishAutomatismes(sessionId);
      setResult(r);
      setPhase('result');
      refresh();
      void qc.invalidateQueries({ queryKey: ['student-skillcards'] });
      void qc.invalidateQueries({ queryKey: ['student-profile'] });
    } catch (e) {
      fail(e);
    }
  }

  const question = play?.questions[index];

  async function answer(choiceId: string) {
    if (!play || !question || revealed || submitting) return;
    setSelected(choiceId);
    setSubmitting(true);
    try {
      const r = await submitAutomatismeAnswer({
        sessionId: play.session.id,
        questionId: question.step_id,
        choiceId,
        timeMs: Date.now() - shownAt.current,
      });
      setRevealed(true);
      const xp = computeXpReward(r.was_correct, streak);
      setStreak(xp.streak);
      setSessionXp((t) => t + xp.total);
      setReward({
        value: xp,
        wasCorrect: r.was_correct,
        explanationMd: r.explanation_md ?? undefined,
        errorFeedbackMd: r.feedback_md ?? undefined,
      });
    } catch (e) {
      fail(e);
    } finally {
      setSubmitting(false);
    }
  }

  function next() {
    if (!play) return;
    if (index < play.questions.length - 1) {
      setIndex((i) => i + 1);
      setSelected(null);
      setRevealed(false);
      shownAt.current = Date.now();
      return;
    }
    void finish(play.session.id);
  }

  if (phase === 'loading' || phase === 'finishing') {
    return (
      <Shell>
        <p role="status" className="text-ink-500 flex items-center gap-2 text-sm">
          <Spinner className="h-4 w-4" />
          {phase === 'finishing' ? 'Calcul de ton score…' : 'Préparation de ta série…'}
        </p>
      </Shell>
    );
  }

  if (phase === 'error') {
    return (
      <Shell>
        <p role="alert" className="text-error-700 bg-error-50 rounded-xl px-4 py-3 text-sm">
          {error}
        </p>
        <Button className="mt-4" fullWidth onClick={() => setPhase('intro')}>
          Retour
        </Button>
      </Shell>
    );
  }

  if (phase === 'result' && result) {
    return (
      <Shell>
        <AutomatismesResultView
          result={result}
          onAgain={() => void begin(false)}
          onDashboard={() => void navigate({ to: '/student/dashboard' })}
        />
      </Shell>
    );
  }

  if (phase === 'playing' && play && question) {
    const total = play.questions.length;
    return (
      <Shell>
        <div className="mb-4 space-y-2">
          <div className="text-ink-600 flex items-center justify-between gap-2 text-xs font-semibold">
            <span>
              Question {index + 1} / {total}
            </span>
            <span className="flex items-center gap-3">
              <span className="flex items-center gap-1" aria-label="Sans calculatrice">
                <Calculator className="h-3.5 w-3.5" aria-hidden /> Sans calculatrice
              </span>
              <span className="flex items-center gap-1 tabular-nums" aria-label="Chrono">
                <Timer className="h-3.5 w-3.5" aria-hidden /> {formatChrono(now - startedAt)}
              </span>
            </span>
          </div>
          <div className="bg-ink-100 h-1.5 overflow-hidden rounded-full">
            <div
              className="bg-primary-500 h-full rounded-full transition-all"
              style={{ width: `${((index + 1) / total) * 100}%` }}
            />
          </div>
          {question.card_title && (
            <p className="text-ink-500 truncate text-xs">
              <MathText>{question.card_title}</MathText>
            </p>
          )}
        </div>
        {paper ? (
          <PaperPage className="-mx-4 min-h-0 sm:rounded-2xl">
            <PaperColumn className="pt-3">
              <div className="text-ink-900 mb-3 text-base">
                <MarkdownMath>{question.prompt_md}</MarkdownMath>
              </div>
              <div className="pp-figure">
                <QuestionVisuals visuals={question.visuals} />
              </div>
              <PaperChoices
                questionKey={question.step_id}
                promptMd={question.prompt_md}
                choices={question.choices}
                selected={selected}
                revealed={revealed}
                busy={submitting}
                onChoose={(id) => void answer(id)}
                storageKey={freeSheetKey('automatismes', play?.session.id, question.step_id)}
              />
            </PaperColumn>
          </PaperPage>
        ) : (
          <>
            <div className="text-ink-900 mb-4 text-base">
              <MarkdownMath>{question.prompt_md}</MarkdownMath>
            </div>
            <QuestionVisuals visuals={question.visuals} />
            <div className="space-y-2">
              {question.choices.map((choice, i) => (
                <ChoiceButton
                  key={choice.id}
                  choice={choice}
                  index={i}
                  selected={selected === choice.id}
                  revealed={revealed}
                  onClick={() => void answer(choice.id)}
                />
              ))}
            </div>
          </>
        )}
        <Button
          variant="ghost"
          fullWidth
          className="mt-6"
          onClick={() => {
            refresh();
            void navigate({ to: '/student/dashboard' });
          }}
        >
          <PauseCircle className="h-4 w-4" aria-hidden /> Arrêter, je reprendrai plus tard
        </Button>
        {reward && (
          <FeedbackPopup
            reward={reward.value}
            wasCorrect={reward.wasCorrect}
            sessionXp={sessionXp}
            explanationMd={reward.explanationMd}
            errorFeedbackMd={reward.errorFeedbackMd}
            onClose={() => {
              setReward(null);
              next();
            }}
          />
        )}
      </Shell>
    );
  }

  // Intro
  const data = state.data;
  const hint = automatismesExamHint(data?.levels?.[data.levels.length - 1]);
  const left = rewardsLeftToday(data);
  return (
    <Shell>
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Zap className="text-primary-600 h-6 w-6" aria-hidden />
          <h1 className="font-display text-ink-900 flex items-center text-2xl font-extrabold">
            Automatismes
            <HelpTip topic="automatismes" label="Automatismes" />
          </h1>
        </div>
        <p className="text-ink-600 text-sm">
          10 questions flash, sans calculatrice, sur ce que tu as déjà vu cette année et les années
          d’avant. Chaque bonne réponse rapporte de l’XP et de l’HORA sur la SKILLCARD concernée.
        </p>
        {hint && (
          <p className="border-primary-100 bg-primary-50/60 ocean:bg-white/5 text-ink-700 rounded-xl border px-3 py-2 text-sm">
            {hint}
          </p>
        )}
        {state.isLoading ? (
          <div className="bg-ink-100 h-12 animate-pulse rounded-xl" />
        ) : !data?.available ? (
          <p className="text-ink-500 text-sm">
            Les automatismes sont réservés aux élèves dont le niveau est indiqué dans le profil.
          </p>
        ) : (
          <>
            <ul className="text-ink-600 space-y-1 text-sm">
              <li>Programme : {automatismesLevelsLabel(data.levels)}</li>
              <li>
                {left > 0
                  ? `Encore ${left} série${left > 1 ? 's' : ''} récompensée${left > 1 ? 's' : ''} en HORA aujourd’hui.`
                  : 'HORA du jour déjà gagnée : les séries suivantes rapportent seulement de l’XP.'}
              </li>
              {data.last?.score_pct != null && (
                <li>
                  Dernière série : {data.last.score_pct} %
                  {data.best_pct != null ? ` · record ${data.best_pct} %` : ''}
                </li>
              )}
            </ul>
            {automatismesLocked(data) ? (
              <AutomatismesUnlock
                userId={user?.id ?? ''}
                level={data.levels?.[data.levels.length - 1] ?? null}
                nextFreeAt={data.access?.next_free_at ?? null}
              />
            ) : (
              <div className="flex flex-col gap-2">
                {data.access && !data.access.unlimited && (
                  <p className="text-ink-500 text-xs">
                    1 série gratuite par mois — illimité avec l’abonnement ou le programme de ton
                    année.
                  </p>
                )}
                <Button fullWidth onClick={() => void begin(false)}>
                  {automatismesCta(data)}
                </Button>
                {data.in_progress && (data.access?.unlimited ?? true) && (
                  <Button variant="outline" fullWidth onClick={() => void begin(true)}>
                    <RotateCw className="h-4 w-4" aria-hidden /> Nouvelle série
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Shell>
  );
}

export function AutomatismesResultView({
  result,
  onAgain,
  onDashboard,
}: {
  result: AutomatismesResult;
  onAgain: () => void;
  onDashboard: () => void;
}) {
  const s = result.session;
  return (
    <div className="space-y-5">
      <div className="text-center">
        <p className="text-ink-500 text-xs font-bold uppercase tracking-wider">Automatismes</p>
        <p className="font-display text-ink-900 text-4xl font-extrabold">{s.score_pct ?? 0}%</p>
        <p className="text-ink-500 text-sm">
          {s.correct ?? 0} / {s.total ?? 0} bonnes réponses · +{s.xp_awarded} XP
          {s.rewarded ? ` · ${formatHoraGain(s.hora_awarded)}` : ''}
        </p>
        {!s.rewarded && (
          <p className="text-ink-500 mt-1 text-xs">
            Entraînement libre : l’HORA du jour est déjà gagnée (3 séries).
          </p>
        )}
      </div>
      {result.cards.length > 0 && (
        <section className="space-y-2">
          <h2 className="font-display text-ink-900 text-base font-bold">SKILLCARDS travaillées</h2>
          <ul className="space-y-2">
            {result.cards.map((c) => (
              <li key={c.card_id}>
                <Link
                  to="/card/$cardId"
                  params={{ cardId: c.card_id }}
                  className="border-ink-200 ocean:bg-white/5 flex min-h-[2.75rem] items-center justify-between gap-3 rounded-xl border bg-white px-4 py-2 text-sm active:scale-[0.99]"
                >
                  <span className="text-ink-900 min-w-0 font-semibold">
                    <MathText>{c.title ?? 'SKILLCARD'}</MathText>
                  </span>
                  <span className="text-ink-500 shrink-0 text-xs">
                    {c.correct}/{c.total}
                    {c.hora_gain > 0 ? ` · ${formatHoraGain(c.hora_gain)}` : ''}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
      <div className="flex flex-col gap-2">
        <Button fullWidth onClick={onAgain}>
          <RotateCw className="h-4 w-4" aria-hidden /> Nouvelle série
        </Button>
        <Button variant="ghost" fullWidth onClick={onDashboard}>
          Retour à l’accueil
        </Button>
      </div>
    </div>
  );
}

/**
 * Série gratuite du mois faite (migration 0339) : date de la prochaine, puis
 * l'illimité — programme de l'année (seul : un pack ne débloque pas les
 * automatismes) ou abonnement. Rien à acheter en natif (App Store 3.1.1).
 */
export function AutomatismesUnlock({
  userId,
  level,
  nextFreeAt,
}: {
  userId: string;
  level: string | null;
  nextFreeAt: string | null;
}) {
  const native = isNativeApp();
  return (
    <div className="space-y-4">
      <div className="border-accent-300 bg-accent-50 ocean:bg-white/5 space-y-1 rounded-2xl border p-4 text-sm">
        <p className="text-ink-900 flex items-center gap-2 font-semibold">
          <Lock className="h-4 w-4" aria-hidden /> Ta série gratuite du mois est faite
        </p>
        <p className="text-ink-700">
          Prochaine série gratuite {nextFreeLabel(nextFreeAt)}. Pour t’entraîner autant que tu veux,
          débloque les automatismes avec le programme de ton année ou l’abonnement.
        </p>
      </div>
      {native ? (
        <p className="text-ink-600 text-sm">
          Le programme de l’année et l’abonnement se prennent sur le site jeraisonne.fr.
        </p>
      ) : (
        <>
          <ProgrammeOffersSection
            userId={userId}
            level={level}
            only="programme"
            returnPath="/student/automatismes"
            title="Avec le programme de ton année"
          />
          <SubscriptionAlternative signedIn returnTo="/student/automatismes" />
        </>
      )}
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-lg px-4 py-6 pb-24 sm:py-8 lg:max-w-2xl">
      <Link
        to="/student/dashboard"
        className="text-primary-600 mb-4 inline-flex min-h-[2.75rem] items-center gap-1 text-sm font-semibold"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden /> Accueil
      </Link>
      {children}
    </div>
  );
}
